/**
 * FloorPlanConfig flow DTO + completeness predicate (PF v2 native module).
 *
 * The module's durable state IS this DTO, stored at
 * `productConfigs["${cartItemId}|floor_plan_config"]` (MODULE-CONTRACT Pattern A).
 *
 * The persisted shape is the EXTENDED `FloorPlanConfigDraftDTO` the legacy adapter
 * serialized (`FloorPlanConfigAdapter.serialize()` = wire `FloorPlanConfigDTO` ∪
 * the "screen IA" fields a resumed configurator needs), NOT the bare wire DTO. All
 * fields are plain JSON already (legacy `.toObject()`/`Array.from` ran at save), so
 * there is no Immutable in the store and wire-compat is automatic: a draft saved
 * under the old adapter hydrates here with zero migration (`PF_VERSION` frozen at 3).
 *
 * Field names are verbatim from the legacy adapter DTO — do NOT rename. The one
 * ADDITIVE key is `floorPlanTemplateType` (PM DECISION D-W2-6): legacy `serialize()`
 * omitted it but completeness gates on it, so we persist it natively (PoA
 * `requiresRepresentative` precedent) AND make the predicate legacy-tolerant so a
 * resumed legacy draft that lacks it does not re-block.
 *
 * ZERO React / store imports — `modules/completeness.ts` (which `store/selectors.ts`
 * imports) pulls `floorPlanConfigIsComplete` from here, so this file stays pure.
 */

import type { PropertyType } from 'models/property'
import { Language } from 'translations/Language'

import {
  FloorPlanColors,
  FloorPlanFileOrientation,
  FloorPlanFormat,
  FloorPlanRenderType,
  FloorPlanSelectionType,
  type FloorPlanProductSettings,
  type Orientations
} from 'constants/floorPlan'

/**
 * Plain-JSON persisted shape — MUST equal what `FloorPlanConfigAdapter.serialize()`
 * emitted (wire `FloorPlanConfigDTO` ∪ the adapter's extended IA fields).
 */
export interface FloorPlanConfigDraftDTO {
  // ── wire DTO (legacy getFloorPlanRequestData) ────────────────────────────────
  /** `FloorPlanColors` value (2D) | `Styles` value (3D). Typed `string` for wire-compat. */
  style: string
  /** 3D only; `undefined` for 2D (legacy dropped it from the wire). */
  orientation?: Orientations
  fixtures: boolean
  furniture: boolean
  roomNames: boolean
  scaleBar: boolean
  compass: boolean
  /** ⚠️ ORIGINAL filename (legacy `setCompanyLogo(file.name)`), NOT the gcFilename. */
  companyLogo: string | null
  /** Draft-only: the logo's stored filename, which a resumed draft needs to show it. The order carries `companyLogo`. */
  companyLogoStoredFilename?: string | null
  /** `Object.keys(floorPlanFormat)` — array of format strings. */
  outputFileFormat: string[]
  outputFileLanguage: Language
  /** Editing-flow per-product settings (legacy `ImmutableMap.toObject()`). */
  floorPlanProductConfig: Record<number, FloorPlanProductSettings>
  dimensions: { showDimensionChain: boolean; showEachRoomArea: boolean }
  /** `Array.from(Set)`; `[]` when 3D. */
  fileOrientation?: FloorPlanFileOrientation[]
  isMultiStoryPlan?: boolean
  gardensAndRoofSlopes?: boolean
  /** Present only when `showDisclaimer` (legacy serialize). */
  disclaimer?: string
  /**
   * Legacy `comment` state. NOTE: legacy `getFloorPlanRequestData` also emits this
   * key (distinct from the wire `clientComment` on `FloorPlanDetailsDTO`).
   */
  clientComment?: string
  // ── adapter IA extension (serialize() extra fields) ──────────────────────────
  currentFloorPlanRenderType?: FloorPlanRenderType
  selectedPropertyType?: PropertyType | null
  showDisclaimer?: boolean
  isActiveDimensions?: boolean
  isActiveLogo?: boolean
  selectedTemplateId?: string | null
  /**
   * ADDITIVE. Template-selection mode. Legacy `serialize()` did NOT persist
   * this; native writes it. Absent on legacy drafts → the predicate falls back to
   * `selectedTemplateId`/`currentFloorPlanRenderType` (legacy-tolerant).
   */
  floorPlanTemplateType?: FloorPlanSelectionType | null
}

/**
 * Read-time fallback for the ops hook's field-merge. NEVER written on mount
 * (read-only-mount invariant, MODULE-CONTRACT §3). Matches the legacy context
 * defaults once serialized: BLACK / 2D / JPG / HORIZONTAL / EN / no logo / no
 * dimensions.
 */
export const FLOOR_PLAN_CONFIG_DEFAULT: FloorPlanConfigDraftDTO = {
  style: FloorPlanColors.BLACK,
  orientation: undefined,
  fixtures: false,
  furniture: false,
  roomNames: false,
  scaleBar: false,
  compass: false,
  companyLogo: null,
  outputFileFormat: [FloorPlanFormat.JPG],
  // Legacy read-fallback was `targetUser.language ?? EN`; EN is the safe default
  // (read-fallback only — never written on mount).
  outputFileLanguage: Language.EN,
  floorPlanProductConfig: {},
  dimensions: { showDimensionChain: false, showEachRoomArea: false },
  fileOrientation: [FloorPlanFileOrientation.HORIZONTAL],
  isMultiStoryPlan: false,
  gardensAndRoofSlopes: false,
  disclaimer: undefined,
  clientComment: '',
  currentFloorPlanRenderType: FloorPlanRenderType.RENDER_2D,
  selectedPropertyType: null,
  showDisclaimer: false,
  isActiveDimensions: false,
  isActiveLogo: false,
  selectedTemplateId: null,
  floorPlanTemplateType: null,
}

/**
 * Pure completeness predicate — the ONLY completeness source. Faithful port of the
 * legacy `isFloorPlanConfigStepValid` (`PurchaseFlowUtilities.context`) MINUS the
 * live `isTemplateLogoUploading` term: a pure DTO predicate cannot read
 * live hub state (§4b), so that "don't proceed while the logo uploads" gate moves
 * to a UI-level guard (`logo.isUploading`) — DF-logo-uploading-gate.
 *
 * Legacy gate:
 *   if (isTemplateLogoUploading) return false   // ← dropped (live hub state)
 *   if (!floorPlanTemplateType)   return false
 *   if (!selectedPropertyType)    return false
 *   if (showDisclaimer && !disclaimerText) return false
 *   return true
 *
 * `templateChosen` is legacy-tolerant: a resumed legacy draft lacks
 * `floorPlanTemplateType`, so a set `selectedTemplateId` / `currentFloorPlanRenderType`
 * also counts as "a template selection was made".
 *
 * NOTE — `companyLogo` is NOT a completeness gate (cleaner than PoA); the logo is a
 * supporting upload only.
 */
/**
 * The configuration as a line that cannot bill a 3D plan takes it: a 3D render type becomes 2D, with
 * the 2D defaults for the fields that differ between the two. A 2D configuration is returned as is.
 */
export const asTwoDimensional = (dto: FloorPlanConfigDraftDTO): FloorPlanConfigDraftDTO =>
  dto.currentFloorPlanRenderType !== FloorPlanRenderType.RENDER_3D
    ? dto
    : {
      ...dto,
      currentFloorPlanRenderType: FloorPlanRenderType.RENDER_2D,
      style: FloorPlanColors.BLACK,
      orientation: undefined,
      fileOrientation: [FloorPlanFileOrientation.HORIZONTAL],
    }

/** Key of a line's single entry in the legacy per-product `floorPlanProductConfig` map. */
export const FLOOR_PLAN_LINE_SETTINGS_KEY = 0

/**
 * An editing line must say what to draw, and name the unit when it is only one (legacy
 * `floor_plan_unit_missing` / `floor_plan_unit_comment_missing`). The configuration page seeds an
 * unanswered entry on every editing line, so a line without one is not an editing line.
 */
export const floorPlanEditingScopeError = (
  dto: Partial<FloorPlanConfigDraftDTO> | undefined
): 'floor_plan_unit_missing' | 'floor_plan_unit_comment_missing' | null => {
  for (const settings of Object.values(dto?.floorPlanProductConfig ?? {})) {
    if (settings.drawOnlySpecificUnit === undefined) return 'floor_plan_unit_missing'
    if (settings.drawOnlySpecificUnit && !settings.drawOnlySpecificUnitComment?.trim()) return 'floor_plan_unit_comment_missing'
  }
  return null
}

const editingScopeIsComplete = (dto: FloorPlanConfigDraftDTO): boolean => floorPlanEditingScopeError(dto) === null

export const floorPlanConfigIsComplete = (dto: FloorPlanConfigDraftDTO | undefined): boolean => {
  if (!dto) return false
  const templateChosen = !!dto.floorPlanTemplateType || !!dto.selectedTemplateId || !!dto.currentFloorPlanRenderType
  if (!templateChosen) return false
  if (!dto.selectedPropertyType) return false
  if (dto.showDisclaimer && !dto.disclaimer) return false
  if (!editingScopeIsComplete(dto)) return false
  return true
}
